import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AIAgent } from '../../types';
import Image from '../common/Image';
import { fetchOrganization, selectOrganization, selectIsUserLeader } from '../../redux/organization/organizationSlice';
import {
  fetchRecentHistory,
  fetchOlderHistory,
  selectAgentGroups,
  selectRecentHistory,
  selectOlderHistory,
  selectFavoriteHistory,
  deleteChat,
  toggleChatFavorite,
  updateChatTitle
} from '../../redux/sidebar/sidebarSlice';
import { AppDispatch } from '../../redux/store';
import { RootState } from '../../redux/store';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Home, PenSquare, Users, ChevronLeft, FileText, Package, ChevronUp, Zap, ImageIcon, Presentation, Video, BookOpen, ChevronDown, ChevronRight, Newspaper, Music, Music2, BarChart3, Plug, Clapperboard, Lock, Smartphone, CheckSquare, FlaskConical, MessagesSquare, ScanSearch } from "lucide-react"
import { reviewSafeText, useMetaLang, SB_STRINGS } from "../../i18n/metaReviewLang"
import UserMenuDropup from './UserMenuDropup';
import HistoryItemMenu from './HistoryItemMenu';
import DeleteChatModal from './DeleteChatModal';
import RenameChatModal from './RenameChatModal';
import CardLockDialog from '../paywall/CardLockDialog';
import trialService from '../../services/api/trialService';
import Toast from '../common/Toast';
import { useLogout } from '../../hooks/useLogout';
import './Sidebar.css';

interface HistoryItem {
  id: number;
  title: string;
  created_at: string;
  first_user_message?: string | null; // 最初のユーザーメッセージを追加 (オプショナル)
  agent_name?: string; // エージェント名を追加
  agent_color?: string; // エージェントの色を追加
  agent_model_type?: string; // エージェントのモデルタイプを追加
  is_favorited?: boolean; // お気に入り状態を追加
}


interface SidebarProps {
  companyName?: string;
  onLogout?: () => void;
  chats?: Array<{ id: number; title: string }>;
  agents?: AIAgent[];
  onSelectAgent?: (agentId: number) => void;
  selectedAgentId?: number | null;
  isLeader?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  collapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  companyName = "",
  onLogout,
  chats = [],
  agents = [],
  onSelectAgent,
  selectedAgentId,
  isLeader = false,
  onToggleCollapse,
  collapsed = false
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const organization = useSelector(selectOrganization);
  const performLogout = useLogout();
  const [recentItems, setRecentItems] = useState<HistoryItem[]>([]);
  const [olderItems, setOlderItems] = useState<HistoryItem[]>([]);
  const [favoriteItems, setFavoriteItems] = useState<HistoryItem[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    material: true,
    ads: true,
    adOperations: true,
    planning: true,
    other: false,
  });
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingChatId, setDeletingChatId] = useState<number | null>(null);
  const [deletingChatTitle, setDeletingChatTitle] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renamingChatId, setRenamingChatId] = useState<number | null>(null);
  const [renamingChatTitle, setRenamingChatTitle] = useState<string>('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  // カード未登録ロック: has_subscription が明示的に false のときだけロックする（取得前・失敗時は fail-open）。
  // ただしリリース前から存在する既存組織(card_lock_exempt)は免除する。
  const [isCardLocked, setIsCardLocked] = useState(false);
  const [cardLockFeature, setCardLockFeature] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === '/jev') {
      setExpandedSections(previous => ({ ...previous, other: true }));
    }
  }, [location.pathname]);

  // 組織情報取得
  useEffect(() => {
    dispatch(fetchOrganization());
  }, [dispatch]);

  // カード登録状況の取得（動画広告・漫画広告のロック判定）
  useEffect(() => {
    let cancelled = false;
    trialService
      .getTrialStatus()
      .then((status) => {
        if (!cancelled)
          setIsCardLocked(status.has_subscription === false && status.card_lock_exempt !== true);
      })
      .catch(() => {
        if (!cancelled) setIsCardLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // デバッグ用: 組織情報の確認
  useEffect(() => {
    console.log('Sidebar - 組織情報デバッグ:', {
      organization,
      hasOrganization: !!organization,
      logoPath: organization?.logo_path,
      organizationName: organization?.name
    });
  }, [organization]);

  // Reduxからのデータを取得
  const reduxAgentGroups = useSelector(selectAgentGroups);
  const reduxRecentHistory = useSelector(selectRecentHistory);
  const reduxOlderHistory = useSelector(selectOlderHistory);
  const reduxFavoriteHistory = useSelector(selectFavoriteHistory);

  // データをローカルステートに反映
  useEffect(() => {
    // エージェントグループの処理は削除
  }, [reduxAgentGroups]);

  useEffect(() => {
    if (reduxRecentHistory.length > 0) {
      setRecentItems(reduxRecentHistory);
    }
  }, [reduxRecentHistory]);

  useEffect(() => {
    if (reduxOlderHistory.length > 0) {
      setOlderItems(reduxOlderHistory);
    }
  }, [reduxOlderHistory]);

  useEffect(() => {
    setFavoriteItems(reduxFavoriteHistory);
  }, [reduxFavoriteHistory]);

  // 履歴データ取得（Redux経由）
  useEffect(() => {
    // 新規チャット画面（/chat/new）の場合は常に履歴を取得
    // または既存のchatsが渡されていない場合のみ履歴を取得
    if (chats.length === 0 || window.location.pathname.includes('/chat/new')) {
      dispatch(fetchRecentHistory());
      dispatch(fetchOlderHistory());
    }
  }, [dispatch, chats.length]);


  // エージェント一覧へ遷移
  const handleAgentListClick = () => {
    navigate('/agents');
  };

  // AIチャットへ遷移
  const handleAiChatClick = () => {
    const timestamp = Date.now();
    // 新しいチャットを開始するためのタイムスタンプをstateに追加
    navigate('/chat/ai', { state: { newChat: timestamp } });
  };

  // チャット履歴項目クリック
  const handleHistoryItemClick = (chatId: number) => {
    navigate(`/chat/${chatId}`);
  };

  // お気に入り切り替え
  const handleToggleFavorite = (chatId: number) => {
    console.log('handleToggleFavorite呼び出し:', chatId);
    dispatch(toggleChatFavorite(chatId)).then((result) => {
      console.log('toggleChatFavorite結果:', result);
    });
  };

  // チャット削除（モーダル表示）
  const handleDeleteChat = (chatId: number) => {
    console.log('handleDeleteChat呼び出し - モーダル表示:', chatId);
    
    // チャットのタイトルを取得
    const allItems = [...favoriteItems, ...recentItems, ...olderItems];
    const chat = allItems.find(item => item.id === chatId);
    const chatTitle = chat?.title || chat?.first_user_message || '無題のチャット';
    
    setDeletingChatId(chatId);
    setDeletingChatTitle(chatTitle);
    setIsDeleteModalOpen(true);
  };

  // チャット削除確認
  const handleDeleteConfirm = () => {
    if (deletingChatId === null) return;
    
    console.log('削除確認 - API呼び出し:', deletingChatId);
    setIsDeleting(true);
    
    dispatch(deleteChat(deletingChatId)).then((result) => {
      console.log('deleteChat結果:', result);
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      
      // 削除後、現在のチャットページにいる場合は新しいチャットページへ遷移
      if (location.pathname === `/chat/${deletingChatId}`) {
        console.log('削除後リダイレクト');
        navigate('/chat/ai');
      }
      
      setDeletingChatId(null);
      setDeletingChatTitle('');
    }).catch((error) => {
      console.error('削除エラー:', error);
      setIsDeleting(false);
    });
  };

  // モーダルを閉じる
  const handleDeleteModalClose = () => {
    console.log('削除モーダルを閉じる');
    setIsDeleteModalOpen(false);
    setDeletingChatId(null);
    setDeletingChatTitle('');
  };

  // 共有ハンドラー
  const handleShare = async (chatId: number) => {
    const url = `${window.location.origin}/chat/${chatId}`;
    try {
      await navigator.clipboard.writeText(url);
      setToastMessage('リンクをコピーしました');
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      console.error('クリップボードへのコピーに失敗:', error);
      setToastMessage('コピーに失敗しました');
      setToastType('error');
      setShowToast(true);
    }
  };

  // タイトル変更ハンドラー（モーダル表示）
  const handleRenameTitle = (chatId: number) => {
    console.log('handleRenameTitle呼び出し:', chatId);

    // チャットのタイトルを取得
    const allItems = [...favoriteItems, ...recentItems, ...olderItems];
    const chat = allItems.find(item => item.id === chatId);
    const chatTitle = chat?.title || chat?.first_user_message || '無題のチャット';

    setRenamingChatId(chatId);
    setRenamingChatTitle(chatTitle);
    setIsRenameModalOpen(true);
  };

  // タイトル変更確認
  const handleRenameConfirm = (newTitle: string) => {
    if (renamingChatId === null) return;

    console.log('タイトル変更確認 - API呼び出し:', renamingChatId, newTitle);
    setIsRenaming(true);

    dispatch(updateChatTitle({ chatId: renamingChatId, title: newTitle })).then((result) => {
      console.log('updateChatTitle結果:', result);
      setIsRenaming(false);
      setIsRenameModalOpen(false);

      if (result.type.endsWith('/fulfilled')) {
        setToastMessage('タイトルを変更しました');
        setToastType('success');
        setShowToast(true);
      } else {
        setToastMessage('タイトルの変更に失敗しました');
        setToastType('error');
        setShowToast(true);
      }

      setRenamingChatId(null);
      setRenamingChatTitle('');
    }).catch((error) => {
      console.error('タイトル変更エラー:', error);
      setIsRenaming(false);
      setToastMessage('タイトルの変更に失敗しました');
      setToastType('error');
      setShowToast(true);
    });
  };

  // タイトル変更モーダルを閉じる
  const handleRenameModalClose = () => {
    console.log('タイトル変更モーダルを閉じる');
    setIsRenameModalOpen(false);
    setRenamingChatId(null);
    setRenamingChatTitle('');
  };

  // AI活用スコアへ遷移
  const handleAiScoreClick = () => {
    console.log('AI活用スコアリンククリック');
    setIsUserMenuOpen(false); // メニューを閉じる
    // React Routerのナビゲーションを使用（SPA形式）
    navigate('/ai-score');
  };

  // AI使用量へ遷移
  const handleUsageClick = () => {
    console.log('AI使用量リンククリック');
    setIsUserMenuOpen(false); // メニューを閉じる
    navigate('/usage');
  };

  // AI資料作成へ遷移
  const handleDocumentCreationClick = () => {
    console.log('AI資料作成リンククリック');
    navigate('/document-creation');
  };

  // Knowledgeへ遷移
  const handleKnowledgeClick = () => {
    console.log('Knowledgeリンククリック');
    navigate('/knowledge');
  };

  // Super Agentへ遷移
  const handleSuperAgentClick = () => {
    console.log('Super Agentリンククリック');
    navigate('/chat/super-agent');
  };

  // 画像生成へ遷移
  const handleImageGenerationClick = () => {
    console.log('画像生成リンククリック');
    navigate('/image-generation');
  };

  // AI資料2（Document V2）へ遷移
  const handleDocumentV2Click = () => {
    console.log('AI資料2リンククリック');
    navigate('/document-v2');
  };

  // AI資料3（Document V3）へ遷移
  const handleDocumentV3Click = () => {
    console.log('AI資料3リンククリック');
    navigate('/document-v3');
  };

  // AI動画資料（Document V3 の kind=video）へ遷移
  const handleVideoDocumentClick = () => {
    navigate('/video-document');
  };

  // セクション折り畳み切り替え
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // 新規ルートへの遷移ハンドラー
  const handleBannerAdsClick = () => {
    navigate('/ads/banner');
  };

  const handleBannerAbTestClick = () => {
    navigate('/ads/banner-ab-tests');
  };

  const handleCarouselAdsClick = () => {
    navigate('/ads/carousel');
  };

  const handleMovieCreationClick = () => {
    navigate('/ads/movie');
  };

  const handleVideoAdsClick = () => {
    if (isCardLocked) {
      setCardLockFeature(sb.videoAds);
      return;
    }
    navigate('/ads/video');
  };

  const handleMangaAdsClick = () => {
    if (isCardLocked) {
      setCardLockFeature(sb.mangaAds);
      return;
    }
    navigate('/ads/manga');
  };

  const handleLpCreationClick = () => {
    navigate('/ads/lp');
  };

  const handleMusicCreationClick = () => {
    navigate('/ads/music');
  };

  const handleSongAdClick = () => {
    if (isCardLocked) {
      setCardLockFeature(sb.songAds);
      return;
    }
    navigate('/ads/song');
  };

  const handleUgcAdClick = () => {
    if (isCardLocked) {
      setCardLockFeature(sb.ugcAds);
      return;
    }
    navigate('/ads/ugc');
  };

  // 動画スタイルレシピ（スタイル動画）。UGC動画広告と同じくカード未登録ロックのみで制御する
  const handleRecipeVideoClick = () => {
    if (isCardLocked) {
      setCardLockFeature(sb.recipeVideo);
      return;
    }
    navigate('/ads/recipe-video');
  };

  const handleNewsClick = () => {
    navigate('/news');
  };

  // Bilingual labels for the Ad Operations links (shared with the Meta-review pages).
  const [metaLang] = useMetaLang();
  const sb = SB_STRINGS[metaLang];

  // 現在のユーザー情報を取得
  const user = useSelector((state: RootState) => state.auth.user);
  const hasConfiguredUserName = Boolean(user?.name && user.name !== '未設定' && user.name !== '名称未設定');
  const rawUserPrimaryName = hasConfiguredUserName
    ? user!.name
    : user?.email ? user.email.split('@')[0] : 'User';
  const rawUserSecondaryName = hasConfiguredUserName
    ? user!.name
    : (user?.email || user?.role || 'Member');
  const displayOrganizationName = reviewSafeText(
    organization?.name || companyName,
    metaLang,
    'Review Organization',
  );
  const displayUserPrimaryName = reviewSafeText(rawUserPrimaryName, metaLang, 'Review User');
  const displayUserSecondaryName = reviewSafeText(rawUserSecondaryName, metaLang, 'Review User');
  const historyDisplayTitle = (item: HistoryItem): string => {
    const rawTitle = (item.title && item.title.trim() !== '' && item.title !== '無題のチャット' && item.title !== 'ChatGPT Agent' && !item.title.includes('Agent'))
      ? item.title
      : (item.first_user_message || sb.untitledChat);

    return reviewSafeText(rawTitle, metaLang, `Chat ${item.id}`);
  };

  // ユーザーがリーダーかどうかを判定
  // 3 経路からチェックする organizationSlice.selectIsUserLeader を使用。
  // 認証時の state.auth.user.role が member のまま組織側で leader に昇格した
  // ケースでも正しく leader 判定でき、「組織管理」メニューが表示される。
  const userIsLeader = useSelector(selectIsUserLeader);
  const isLeaderRole = userIsLeader;

  // 組織編集画面へ遷移
  const handleEditOrganizationClick = () => {
    console.log('組織編集アイコンクリック');
    setIsUserMenuOpen(false); // メニューを閉じる
    // デバッグ情報を詳細に出力
    console.log('権限チェックデバッグ情報:', {
      user,
      userType: typeof user,
      userRole: user?.role,
      userRoleType: typeof user?.role,
      isLeaderFromProps: isLeader,
      userIsLeader,
      isLeaderRole
    });

    // リーダー権限チェックを直接行う（大文字小文字を無視して比較）
    const hasLeaderRole =
      typeof user?.role === 'string' &&
      (user.role.toLowerCase() === 'leader' || user.role.toLowerCase() === 'リーダー');

    // isLeaderプロパティがtrueの場合かユーザーがリーダー権限を持っている場合
    if (isLeader === true || hasLeaderRole) {
      console.log('リーダー権限確認OK - 編集画面へ遷移します');
      navigate('/organization/edit');
    } else {
      console.log('リーダー権限がないため編集画面へ遷移できません');
      console.log('ユーザーロール:', user?.role);
    }
  };

  // アカウント設定画面へ遷移
  const handleAccountSettingsClick = () => {
    console.log('アカウント設定リンククリック');
    setIsUserMenuOpen(false); // メニューを閉じる
    navigate('/account/settings');
  };

  // ログアウト処理のラッパー
  // props.onLogout が渡されていないページでも確実にログアウトできるよう、
  // フォールバックとして Sidebar 内蔵の useLogout を使う。
  const handleLogoutClick = () => {
    setIsUserMenuOpen(false); // メニューを閉じる
    if (onLogout) {
      onLogout();
    } else {
      performLogout();
    }
  };

  // メニュー外クリック検知
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  return (
    <aside data-tutorial="home-sidebar" className={`sidebar ${collapsed ? 'w-0' : 'w-64'} bg-warm-card border-r border-warm-border flex flex-col transition-all duration-300 overflow-hidden h-full min-h-0`}>
      {/* ヘッダー */}
      <div className="shrink-0 pt-4 pb-3 px-4 border-b border-warm-border flex items-center justify-between">
        <Link to="/" className="flex justify-start">
          <div className="w-[120px] h-auto relative">
            {organization?.logo_path ? (
              <img 
                src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/storage/${organization.logo_path}`}
                alt={`${displayOrganizationName} Logo`}
                className="logo object-contain"
                style={{ maxHeight: '40px', objectFit: 'contain' }}
              />
            ) : (
              <Image category="common" fileName="logo.png" alt="Logo" className="logo object-contain" />
            )}
          </div>
        </Link>
        {/* 折り畳みボタン */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleCollapse?.(true)}
          className="p-1 h-8 w-8 hover:bg-warm-hover rounded-md"
          title={sb.collapseSidebar}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
      </div>

      {/* 会社名 */}
      {(organization?.name || companyName) && (
        <div className="shrink-0 px-4 py-2 text-sm text-gray-600 border-b border-gray-300 mb-4">
          {displayOrganizationName}
        </div>
      )}

      <Link to="/home" aria-current={location.pathname === '/home' ? 'page' : undefined} className={`mx-3 flex shrink-0 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${location.pathname === '/home' ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:bg-warm-hover'}`}>
        <Home className="h-4 w-4" />{metaLang === 'en' ? 'Home' : 'ホーム'}
      </Link>
      {/* 新しいチャットボタン (Tasky primary solid) */}
      <div className="shrink-0 p-3">
        <Button
          variant="default"
          onClick={handleAiChatClick}
          className={`w-full justify-start text-sm font-medium h-11 bg-primary hover:bg-primary-hover text-white border-0 shadow-sm`}
        >
          <PenSquare className="w-4 h-4 mr-3" />
          {sb.newChat}
        </Button>
        {/* チャットv2（選択肢カードで進めるチャット）。チャットAIツールが有効な組織だけ */}
        {organization?.enable_chat_tools && (
          <Button
            variant="ghost"
            onClick={() => navigate('/chat-v2')}
            aria-current={location.pathname.startsWith('/chat-v2') ? 'page' : undefined}
            data-testid="sidebar-chat-v2"
            className={`mt-1 w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/chat-v2') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
          >
            <MessagesSquare className="w-4 h-4 mr-3" />
            <span className="link-text">{sb.chatV2}</span>
          </Button>
        )}
      </div>

      {/* Navigation + Chat history share the remaining height; ensures footer (user menu) stays reachable */}
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">

      {/* Collapsible Navigation Sections */}
      <div className="shrink-0 px-3 space-y-0">

        {/* 広告制作 Section (default: expanded) */}
        <div className="mt-2" data-tutorial="home-sidebar-ads">
          <button
            type="button"
            onClick={() => toggleSection('ads')}
            className="flex items-center justify-between w-full py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors px-2"
          >
            <span>{sb.sectionAds}</span>
            {expandedSections.ads ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {expandedSections.ads && (
          <div className="space-y-1 mt-1">
            <Button
              variant="ghost"
              onClick={handleBannerAdsClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/ads/banner' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <ImageIcon className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.bannerAds}</span>
            </Button>
            {organization?.enable_auto_banner_ab_test && (
              <Button
                variant="ghost"
                onClick={handleBannerAbTestClick}
                className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/ads/banner-ab-tests') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
              >
                <FlaskConical className="w-4 h-4 mr-3" />
                <span className="link-text">{sb.bannerAbTest}</span>
              </Button>
            )}
            {organization?.enable_carousel_creation && (
              <Button
                variant="ghost"
                onClick={handleCarouselAdsClick}
                className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/ads/carousel' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
              >
                <ImageIcon className="w-4 h-4 mr-3" />
                <span className="link-text">{sb.carouselAds}</span>
              </Button>
            )}
            {organization?.enable_movie_creation && (
              <Button
                variant="ghost"
                onClick={handleMovieCreationClick}
                className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/ads/movie') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
              >
                <Clapperboard className="w-4 h-4 mr-3" />
                <span className="link-text">{sb.movieCreation}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleVideoAdsClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/ads/video' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <Video className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.videoAds}</span>
              {isCardLocked && <Lock className="w-3.5 h-3.5 ml-auto text-gray-400" />}
            </Button>
            <Button
              variant="ghost"
              onClick={handleMangaAdsClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/ads/manga' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <BookOpen className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.mangaAds}</span>
              {isCardLocked && <Lock className="w-3.5 h-3.5 ml-auto text-gray-400" />}
            </Button>
            {organization?.enable_lp_creation && (
              <Button
                variant="ghost"
                onClick={handleLpCreationClick}
                className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/ads/lp' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
              >
                <FileText className="w-4 h-4 mr-3" />
                <span className="link-text">{sb.lpCreation}</span>
              </Button>
            )}
            {/* LP改善ツール（β）。LP制作と同じくカードロックの対象外・組織フラグなし */}
            <Button
              variant="ghost"
              onClick={() => navigate('/lp-improve')}
              aria-current={location.pathname.startsWith('/lp-improve') ? 'page' : undefined}
              data-testid="sidebar-lp-improve"
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/lp-improve') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <ScanSearch className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.lpImprove}</span>
            </Button>
            {organization?.enable_music_creation && (
              <Button
                variant="ghost"
                onClick={handleMusicCreationClick}
                className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/ads/music' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
              >
                <Music className="w-4 h-4 mr-3" />
                <span className="link-text">{sb.musicCreation}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleSongAdClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/ads/song') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <Music2 className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.songAds}</span>
              {isCardLocked && <Lock className="w-3.5 h-3.5 ml-auto text-gray-400" />}
            </Button>
            {/* UGC動画広告は動画広告・漫画広告と同じくカード未登録ロックのみで制御する。
                組織ごとのフラグでは出し分けない（DBカラムとAPIのフィールドは残してある）。 */}
            <Button
              variant="ghost"
              onClick={handleUgcAdClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/ads/ugc') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <Smartphone className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.ugcAds}</span>
              {isCardLocked && <Lock className="w-3.5 h-3.5 ml-auto text-gray-400" />}
            </Button>
            <Button
              variant="ghost"
              onClick={handleRecipeVideoClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/ads/recipe-video') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <Clapperboard className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.recipeVideo}</span>
              {isCardLocked && <Lock className="w-3.5 h-3.5 ml-auto text-gray-400" />}
            </Button>
          </div>
        )}

        {organization?.enable_ad_operations && (
          <>
            {/* 広告運用 Section (TAAP Phase1) */}
            <div className="mt-2">
              <button
                type="button"
                onClick={() => toggleSection('adOperations')}
                className="flex items-center justify-between w-full py-1.5 text-xs font-semibold text-gray-500 tracking-wider hover:text-gray-700 transition-colors"
              >
                <span>{sb.adOps}</span>
                {expandedSections.adOperations ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
            {expandedSections.adOperations && (
              <div className="space-y-1 mt-1">
                {/* TAAP: 同期済み実績で動く運用ダッシュボードと改善提案 */}
                <Button
                  variant="ghost"
                  onClick={() => navigate('/taap')}
                  className={`taap-dashboard-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${(location.pathname.startsWith('/taap') || location.pathname.startsWith('/ad-dashboard') || location.pathname === '/settings/media-connect') ? 'bg-[#F0EDE4]' : 'hover:bg-[#F0EDE4]'}`}
                >
                  <BarChart3 className="w-4 h-4 mr-3" />
                  <span className="link-text">{sb.taapDashboard}</span>
                </Button>
              </div>
            )}
          </>
        )}

        {/* 素材制作 Section (default: expanded) */}
        <div className="mt-2">
          <button
            type="button"
            onClick={() => toggleSection('material')}
            className="flex items-center justify-between w-full py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors px-2"
          >
            <span>{sb.sectionMaterial}</span>
            {expandedSections.material ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {expandedSections.material && (
          <div className="space-y-1 mt-1">
            <Button
              variant="ghost"
              onClick={handleImageGenerationClick}
              className={`image-generation-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/image-generation' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <ImageIcon className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.imageGeneration}</span>
            </Button>
          </div>
        )}

        {/* プランニング Section (default: expanded) */}
        <div className="mt-2">
          <button
            type="button"
            onClick={() => toggleSection('planning')}
            className="flex items-center justify-between w-full py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors px-2"
          >
            <span>{sb.sectionPlanning}</span>
            {expandedSections.planning ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {expandedSections.planning && (
          <div className="space-y-1 mt-1">
            <Button
              variant="ghost"
              onClick={handleAgentListClick}
              className={`agent-list-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/agents' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <Users className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.agentList}</span>
            </Button>
            {/* 商品ページ/一覧 - 組織設定で有効化されている場合のみ表示 */}
            {organization?.enable_product_catalog && (
              <Button
                variant="ghost"
                onClick={handleKnowledgeClick}
                className={`knowledge-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/knowledge' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
              >
                <Package className="w-4 h-4 mr-3" />
                <span className="link-text">{sb.productList}</span>
              </Button>
            )}
          </div>
        )}

        {/* その他 Section (default: collapsed) */}
        {(
          <>
            <div className="mt-2">
              <button
                type="button"
                onClick={() => toggleSection('other')}
                className="flex items-center justify-between w-full py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors px-2"
              >
                <span>{sb.sectionOther}</span>
                {expandedSections.other ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
            {expandedSections.other && (
              <div className="space-y-1 mt-1">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/jev')}
                  aria-current={location.pathname === '/jev' ? 'page' : undefined}
                  className={`jev-evaluation-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/jev' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
                >
                  <FlaskConical className="w-4 h-4 mr-3" />
                  <span className="link-text">{sb.jevEvaluation}</span>
                </Button>
                {/* AI資料 - 組織設定で有効化されている場合のみ表示 */}
                {organization?.enable_ai_knowledge && (
                  <Button
                    variant="ghost"
                    onClick={handleDocumentCreationClick}
                    className={`ai-document-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/document-creation' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
                  >
                    <FileText className="w-4 h-4 mr-3" />
                    <span className="link-text">{sb.aiDocument}</span>
                  </Button>
                )}
                {/* AI資料2 - 組織設定で有効化されている場合のみ表示 */}
                {organization?.enable_ai_knowledge && (
                  <Button
                    variant="ghost"
                    onClick={handleDocumentV2Click}
                    className={`document-v2-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/document-v2' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
                  >
                    <Presentation className="w-4 h-4 mr-3" />
                    <span className="link-text">{sb.aiDocument2}</span>
                  </Button>
                )}
                {/* AI資料3 - 組織設定で有効化されている場合のみ表示 */}
                {organization?.enable_ai_doc3 && (
                  <Button
                    variant="ghost"
                    onClick={handleDocumentV3Click}
                    className={`document-v3-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/document-v3') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
                  >
                    <Presentation className="w-4 h-4 mr-3" />
                    <span className="link-text">{sb.aiDocument3}</span>
                  </Button>
                )}
                {/* AI動画資料 - AI資料3 と同じ組織設定で表示 */}
                {organization?.enable_ai_doc3 && (
                  <Button
                    variant="ghost"
                    onClick={handleVideoDocumentClick}
                    className={`video-document-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/video-document') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
                  >
                    <Clapperboard className="w-4 h-4 mr-3" />
                    <span className="link-text">{sb.aiVideoDocument}</span>
                  </Button>
                )}
                {/* Super Agent - 組織設定で有効化されている場合のみ表示 */}
                {organization?.enable_super_agent && (
                  <Button
                    variant="ghost"
                    onClick={handleSuperAgentClick}
                    className={`super-agent-link w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname === '/chat/super-agent' ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
                  >
                    <Zap className="w-4 h-4 mr-3" />
                    <span className="link-text">Super Agent</span>
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {organization?.enable_news && (
          <div className="mt-2" data-tutorial="home-sidebar-news">
            <Button
              variant="ghost"
              onClick={handleNewsClick}
              className={`w-full justify-start text-sm font-normal h-9 text-gray-700 rounded-lg ${location.pathname.startsWith('/news') ? 'bg-warm-hover' : 'hover:bg-warm-hover'}`}
            >
              <Newspaper className="w-4 h-4 mr-3" />
              <span className="link-text">{sb.news}</span>
            </Button>
          </div>
        )}

      </div>

      {/* 履歴セクション */}
      <div className="shrink-0 px-3 mt-4 mb-2" data-tutorial="chat-history">
        <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-2">{sb.chatHistory}</h3>
      </div>
        <ScrollArea className="min-h-[120px] shrink-0 px-3 pb-3">
          <div className="">
            {/* お気に入りセクション */}
            {favoriteItems.length > 0 && (
              <>
                <div className="px-3 mb-2">
                  <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-2">{sb.favorites}</h3>
                </div>
                {favoriteItems.map((item) => (
                  <div key={item.id} className="group relative mb-1 overflow-visible px-3">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-sm font-normal h-12 text-gray-700 hover:bg-warm-hover transition-colors rounded-lg pr-2"
                      onClick={() => handleHistoryItemClick(item.id)}
                    >
                      <div className="flex flex-col items-start w-full pr-2">
                        <span className="truncate text-left w-title-width">
                          {historyDisplayTitle(item)}
                        </span>
                      </div>
                    </Button>
                    <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10 sidebar-menu-container">
                      <HistoryItemMenu
                        chatId={item.id}
                        chatTitle={historyDisplayTitle(item)}
                        isFavorited={item.is_favorited}
                        onToggleFavorite={handleToggleFavorite}
                        onDelete={handleDeleteChat}
                        onShare={handleShare}
                        onRenameTitle={handleRenameTitle}
                      />
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* 過去7日間 */}
            <div className="px-3 mb-2 mt-4">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-2">{sb.past7days}</h3>
            </div>
            {recentItems.map((item) => (
              <div key={item.id} className="group relative mb-1 overflow-visible px-3">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-sm font-normal h-12 text-gray-700 hover:bg-warm-hover transition-colors rounded-lg pr-2"
                  onClick={() => handleHistoryItemClick(item.id)}
                >
                  <div className="flex flex-col items-start w-full pr-2">
                    <span className="truncate text-left w-title-width">
                      {historyDisplayTitle(item)}
                    </span>
                  </div>
                </Button>
                <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10 sidebar-menu-container">
                  <HistoryItemMenu
                    chatId={item.id}
                    chatTitle={historyDisplayTitle(item)}
                    isFavorited={item.is_favorited}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={handleDeleteChat}
                    onShare={handleShare}
                    onRenameTitle={handleRenameTitle}
                  />
                </div>
              </div>
            ))}

            {/* 過去30日間 */}
            <div className="px-3 mt-4 mb-2">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-2">{sb.past30days}</h3>
            </div>
            {olderItems.map((item) => (
              <div key={item.id} className="group relative mb-1 overflow-visible px-3">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-sm font-normal h-12 text-gray-700 hover:bg-warm-hover transition-colors rounded-lg pr-2"
                  onClick={() => handleHistoryItemClick(item.id)}
                >
                  <div className="flex flex-col items-start w-full pr-2">
                    <span className="truncate text-left w-title-width">
                      {historyDisplayTitle(item)}
                    </span>
                  </div>
                </Button>
                <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10 sidebar-menu-container">
                  <HistoryItemMenu
                    chatId={item.id}
                    chatTitle={historyDisplayTitle(item)}
                    isFavorited={item.is_favorited}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={handleDeleteChat}
                    onShare={handleShare}
                    onRenameTitle={handleRenameTitle}
                  />
                </div>
              </div>
            ))}
          </div>
      </ScrollArea>
      </div>

       {/* User Profile */}
       {user && (
         <div className="relative shrink-0 p-3 border-t border-warm-border mt-auto" ref={userMenuRef} data-tutorial="home-sidebar-account">
           {/* ドロップアップメニュー */}
           {isUserMenuOpen && (
             <UserMenuDropup
               isLeader={isLeaderRole}
               onOrganizationClick={handleEditOrganizationClick}
               onAccountSettingsClick={handleAccountSettingsClick}
               onAiScoreClick={handleAiScoreClick}
               onUsageClick={handleUsageClick}
               onLogout={handleLogoutClick}
             />
           )}

           {/* ユーザープロフィールボタン */}
           <Button
             variant="ghost"
             className="w-full justify-start text-sm font-normal h-auto py-2.5 px-2 hover:bg-warm-surface rounded-lg"
             onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
           >
             <div className="w-9 h-9 bg-gradient-to-br from-primary to-purple rounded-full flex items-center justify-center mr-2.5 flex-shrink-0">
               <span className="text-white text-sm font-semibold">
                 {organization?.name
                   ? displayOrganizationName.charAt(0).toUpperCase()
                   : displayUserPrimaryName.charAt(0).toUpperCase()}
               </span>
             </div>
             <div className="flex flex-col items-start flex-1 min-w-0">
               <span className="text-sm font-semibold text-dark truncate max-w-full">
                 {organization?.name ? displayOrganizationName : displayUserPrimaryName}
               </span>
               <span className="text-xs text-gray-500 truncate max-w-full">
                 {displayUserSecondaryName}
               </span>
             </div>
             {/* 展開アイコン */}
             <ChevronUp className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isUserMenuOpen ? '' : 'rotate-180'}`} />
           </Button>
         </div>
       )}

       {/* 削除確認モーダル */}
       <DeleteChatModal
         isOpen={isDeleteModalOpen}
         onClose={handleDeleteModalClose}
         onConfirm={handleDeleteConfirm}
         chatTitle={deletingChatTitle}
         isDeleting={isDeleting}
       />

       {/* タイトル変更モーダル */}
       <RenameChatModal
         isOpen={isRenameModalOpen}
         onClose={handleRenameModalClose}
         onConfirm={handleRenameConfirm}
         currentTitle={renamingChatTitle}
         isRenaming={isRenaming}
       />

       {/* カード未登録ロックの誘導ダイアログ */}
       <CardLockDialog
         open={cardLockFeature !== null}
         featureName={cardLockFeature ?? ''}
         onClose={() => setCardLockFeature(null)}
         onRegister={() => {
           setCardLockFeature(null);
           navigate('/plans');
         }}
       />

       {/* Toast通知 */}
       {showToast && (
         <Toast
           message={toastMessage}
           type={toastType}
           duration={3000}
           onClose={() => setShowToast(false)}
         />
       )}
    </aside>
  );
};

export default Sidebar;
